'use client';

import { type ReactNode } from 'react';
import type { ErrorCode, FieldError } from '@icebreaker/shared/errors';
import { fieldErrorSentence, fieldErrorTexts, loadErrorKey } from '@icebreaker/shared/i18n';
import { useTranslation } from './i18n-context';

/**
 * HATA METNİ DİKİŞİ — mobildeki `shared/ui/StateViews` bileşeninin web eşi.
 *
 * QA ara turu: web'de 21 dosyada 28 çağrı yeri `t.t(\`errors.${code}\`)` yazıyordu.
 * Bu üç ayrı kusur üretiyordu:
 *   1. Sözlükte karşılığı olmayan kod ekrana HAM kod olarak düşüyordu
 *      (üretimde `strict: false` → anahtar adı basılır).
 *   2. İstek sunucuya HİÇ ULAŞMADIĞINDA (`statusCode === 0`) istemcinin
 *      uydurduğu `INTERNAL_ERROR` "beklenmeyen bir hata" diye yazılıyordu;
 *      mobil aynı koşulda "bağlantı yok" diyordu — iki uygulama ayrışmıştı.
 *   3. Kural 28 yerde kopyalanmıştı; yeni ekran 29.'yu yazıyordu.
 *
 * Kural artık TEK yerdedir: `loadErrorKey` (packages/shared). Web üretim kodunda
 * ham `errors.${...}` yazmak `error-message.guard.spec.ts` tarafından yasaktır.
 */

/**
 * Dikişin girdisi: tam `ApiErrorBody`, yalnızca kod taşıyan bir kırıntı ya da
 * çıplak kodun kendisi (`ApiErrorBody` bu tipe atanabilirdir).
 */
export interface ErrorLike {
  readonly code: string;
  readonly statusCode?: number | null;
}

function codeOf(error: ErrorLike | string | null | undefined): string | null {
  if (!error) return null;
  return typeof error === 'string' ? error : error.code;
}

function statusOf(error: ErrorLike | string | null | undefined): number | null {
  if (!error || typeof error === 'string') return null;
  return typeof error.statusCode === 'number' ? error.statusCode : null;
}

/**
 * Hata kodunu kullanıcı cümlesine çeviren TEK yol.
 *
 * Hata yoksa `null` döner — çağıran kendi bağlam metnine düşebilsin diye
 * (`attendeeList.loadError` gibi). Kod BİLİNMİYORSA ham basılmaz, ortak
 * `INTERNAL_ERROR` cümlesine düşer.
 */
export function useErrorMessage(): (error: ErrorLike | string | null | undefined) => string | null {
  const t = useTranslation();
  return (error) => {
    const code = codeOf(error);
    if (code === null || code === undefined) return null;
    return t.t(loadErrorKey(code, statusOf(error)));
  };
}

/**
 * ALAN BAZLI hata cümleleri — jenerik koddan SONRA yazılır.
 *
 * QA Blok 30 · BULGU-1: sunucu `fieldErrors: [{ path: 'venue.cityCode',
 * rule: 'event.venueCityRequired' }]` döndürüyordu; ekran YALNIZ `code`u
 * çeviriyor ("Girdiğin bilgilerde hata var.") ve hangi alan olduğunu
 * söylemiyordu. Sözlükteki `validation.*` bölümü hiç kullanılmıyordu.
 *
 * Cümleyi kuran kural `packages/shared` içindedir (`fieldErrorTexts`), üç
 * uygulama da oradan beslenir; burada yalnız çeviri yapılır. Bilinmeyen kural
 * veya adsız alan satır ÜRETMEZ — çağıran jenerik cümlede kalır (md. 9).
 */
export function useFieldErrorMessages(): (
  error: { readonly fieldErrors?: readonly FieldError[] } | null | undefined,
) => readonly string[] {
  const t = useTranslation();
  return (error) =>
    // Sayılar yerel biçimle ("(1.000)"), cümleyi kuran tek yardımcı (QA P3, 22.10.2026).
    fieldErrorTexts(error ? { fieldErrors: [...(error.fieldErrors ?? [])] } : null).map((item) =>
      fieldErrorSentence(t, item),
    );
}

export interface ErrorTextProps {
  readonly error: ErrorLike | ErrorCode | null | undefined;
  /** Çağıranın kendi ölçü/renk sınıfı — görünüm özellik modülünde kalır. */
  readonly className?: string;
  /** README 4.8: hata satırında ikon kullanan ekranlar onu buradan geçirir. */
  readonly icon?: ReactNode;
}

/** Satır içi hata cümlesi (`<p role="alert">`) — en sık kullanılan biçim. */
export function ErrorText({ error, className, icon }: ErrorTextProps) {
  const message = useErrorMessage()(error);
  if (message === null) return null;
  return (
    <p className={className} role="alert">
      {icon}
      {message}
    </p>
  );
}

export interface ErrorBoxProps extends Omit<ErrorTextProps, 'icon'> {
  /** Kurtarma eylemi (yeniden dene) — cümleden SONRA çizilir. */
  readonly children?: ReactNode;
}

/** Kutu biçimi: cümle + kurtarma eylemi (`<div role="alert">`). */
export function ErrorBox({ error, className, children }: ErrorBoxProps) {
  const message = useErrorMessage()(error);
  if (message === null) return null;
  return (
    <div className={className} role="alert">
      {message}
      {children}
    </div>
  );
}
